/**
 * Identity-conflict detection and resolution for snapshot import.
 *
 * Matching key is `(cpid, wsUrl)` per CLAUDE.md §4.2 — never cpid alone.
 * The three resolutions match the YAML spec:
 *   - Replace          → overwrite the existing station's data, keep its id.
 *   - SaveAsCopy       → assign a fresh stationId; caller provides new
 *                        (cpid, wsUrl) so the pair is unique.
 *   - Cancel           → abort the import.
 *
 * This module is pure logic + repo lookup; the UI decides which resolution
 * to apply and feeds the choice back to `importSnapshot`.
 */
import type { StationRepository } from '@/persistence/ports';
import type { SnapshotV1 } from './snapshotSchema';

export const ConflictResolution = {
  Replace: 'replace',
  SaveAsCopy: 'save-as-copy',
  /**
   * FEAT-007 (Session 8f.8) — pull the snapshot's payload (configurations,
   * profiles, queue per Include toggles) into the CURRENTLY OPEN station,
   * leaving its (cpid, wsUrl, ocppVersion, stationId) untouched. Only
   * meaningful when the UI has a live station session.
   */
  ApplyToOpen: 'apply-to-open',
  Cancel: 'cancel',
} as const;
export type ConflictResolution =
  (typeof ConflictResolution)[keyof typeof ConflictResolution];

export interface ConflictDetected {
  readonly kind: 'conflict';
  readonly existingStationId: string;
  readonly cpid: string;
  readonly wsUrl: string;
}

export interface NoConflict {
  readonly kind: 'clear';
}

export type ConflictCheckResult = ConflictDetected | NoConflict;

export async function detectConflict(
  repo: StationRepository,
  snap: SnapshotV1,
): Promise<ConflictCheckResult> {
  const existing = await repo.findByIdentity(snap.station.cpid, snap.station.wsUrl);
  if (!existing) return { kind: 'clear' };
  return {
    kind: 'conflict',
    existingStationId: existing.id,
    cpid: existing.cpid,
    wsUrl: existing.wsUrl,
  };
}

export interface SaveAsCopyOverride {
  readonly resolution: typeof ConflictResolution.SaveAsCopy;
  /** New cpid OR wsUrl (or both) so the pair becomes unique. */
  readonly cpid?: string;
  readonly wsUrl?: string;
}

export interface ReplaceChoice {
  readonly resolution: typeof ConflictResolution.Replace;
}

export interface CancelChoice {
  readonly resolution: typeof ConflictResolution.Cancel;
}

export interface ApplyToOpenChoice {
  readonly resolution: typeof ConflictResolution.ApplyToOpen;
  /** StationId of the currently open station; identity stays at its values. */
  readonly targetStationId: string;
  /** Per-section toggles — same shape as Export's `include`. */
  readonly include?: {
    readonly configuration?: boolean;
    readonly chargingProfiles?: boolean;
    readonly offlineQueue?: boolean;
    readonly deviceModel?: boolean;
    readonly localAuthList?: boolean;
  };
}

export type ConflictChoice =
  | ReplaceChoice
  | SaveAsCopyOverride
  | ApplyToOpenChoice
  | CancelChoice;

/**
 * Validate the Save-as-copy override against the existing match. Returns
 * the new (cpid, wsUrl) pair, or an error message if the override does not
 * actually resolve the conflict (same pair as before).
 */
export function applySaveAsCopy(
  conflict: ConflictDetected,
  override: SaveAsCopyOverride,
): { readonly ok: true; readonly cpid: string; readonly wsUrl: string }
  | { readonly ok: false; readonly error: string } {
  const nextCpid = override.cpid?.trim() !== undefined && override.cpid?.trim() !== ''
    ? override.cpid.trim()
    : conflict.cpid;
  const nextWsUrl = override.wsUrl?.trim() !== undefined && override.wsUrl?.trim() !== ''
    ? override.wsUrl.trim()
    : conflict.wsUrl;
  if (nextCpid === conflict.cpid && nextWsUrl === conflict.wsUrl) {
    return {
      ok: false,
      error: 'Save-as-copy requires a different cpid or wsUrl from the existing station.',
    };
  }
  return { ok: true, cpid: nextCpid, wsUrl: nextWsUrl };
}
